import React, { useEffect, useMemo, useState } from "react";
import { Building2, FileBarChart2, ShoppingCart, Users } from "lucide-react";
import { DashboardCards } from "../../components/dashboard/DashboardCards";
import { AdminLatestTables } from "./components/AdminLatestTables";
import { AdminSalesOverviewChart } from "./components/AdminSalesOverviewChart";
import { SalesByBusinessPieChart } from "./components/SalesByBusinessPieChart";
import { RecentActivity } from "../../app/components/dashboard/RecentActivity";
import { mapRecentActivityToFeed } from "../../app/components/dashboard/mapRecentActivityFeed";
import { api, type AdminDashboardResponse } from "../../lib/api";
import { formatInrWhole } from "../../lib/currency";
import { getAlignedIsoWeekVsPreviousRanges } from "../../lib/isoWeekRanges";
import { useAuth } from "../../providers/AuthProvider";
import type { DashboardCardItem } from "../../components/dashboard/DashboardCards";

export function AdminDashboardPage() {
  const { token } = useAuth();
  const [cards, setCards] = useState<DashboardCardItem[]>([]);
  const [dashboardPayload, setDashboardPayload] = useState<AdminDashboardResponse["data"] | null>(null);
  const [businessList, setBusinessList] = useState<
    Array<{ name: string; owner: string; status: string; created: string }>
  >([]);

  const fmtMoney = useMemo(
    () => (v: string | number | null | undefined) => {
      const n = typeof v === "number" ? v : Number(v ?? 0);
      return formatInrWhole(n);
    },
    [],
  );

  const pctDelta = (curr: number, prev: number) => {
    if (prev === 0) return { value: 0, isPositive: true };
    const pct = ((curr - prev) / prev) * 100;
    return { value: Math.round(pct * 10) / 10, isPositive: pct >= 0 };
  };

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const { current: curRange, previous: prevRange } = getAlignedIsoWeekVsPreviousRanges(new Date());

        const [cur, prev, businessesRes] = await Promise.all([
          api.dashboard.getAdmin({
            token,
            filters: { from_date: curRange.from, to_date: curRange.to },
          }),
          api.dashboard.getAdmin({
            token,
            filters: { from_date: prevRange.from, to_date: prevRange.to },
          }),
          api.admin.getBusinesses({ token }),
        ]);

        if (cancelled) return;

        const curJson = cur as AdminDashboardResponse;
        const prevJson = prev as AdminDashboardResponse;
        setDashboardPayload(curJson.data);

        const relativeTime = (iso: string | null | undefined) => {
          if (!iso) return "—";
          const t = new Date(iso).getTime();
          if (!Number.isFinite(t)) return "—";
          const msAgo = Date.now() - t;
          const s = Math.floor(msAgo / 1000);
          const m = Math.floor(s / 60);
          const h = Math.floor(m / 60);
          const dday = Math.floor(h / 24);
          if (s < 60) return `${s}s ago`;
          if (m < 60) return `${m}m ago`;
          if (h < 24) return `${h}h ago`;
          return `${dday}d ago`;
        };

        setBusinessList(
          ((businessesRes as any[]) ?? []).slice(0, 5).map((b) => ({
            name: String(b.name ?? ""),
            owner: String(b.ownerName ?? b.ownerEmail ?? "—"),
            status: String(b.status ?? "pending"),
            created: relativeTime(b.verifiedAt ?? null),
          })),
        );

        const curData = curJson.data.platform_overview;
        const prevData = prevJson.data.platform_overview;

        const totalBusinessOwners = String(curData.total_businesses ?? 0);
        const pendingVerifications = String(curData.pending_businesses ?? 0);

        const curSales = Number(curData.total_gmv ?? 0);
        const prevSales = Number(prevData.total_gmv ?? 0);
        const salesTrend = pctDelta(curSales, prevSales);

        const curOrders = Number(curData.total_orders ?? 0);
        const prevOrders = Number(prevData.total_orders ?? 0);
        const ordersTrend = pctDelta(curOrders, prevOrders);

        setCards([
          {
            title: "Total business owners",
            value: totalBusinessOwners,
            icon: Users,
            iconColor: "bg-blue-600",
          },
          {
            title: "Pending verifications",
            value: pendingVerifications,
            icon: Building2,
            iconColor: "bg-amber-600",
          },
          {
            title: "Platform sales (WTD)",
            value: fmtMoney(curSales),
            icon: ShoppingCart,
            trend: { value: salesTrend.value, isPositive: salesTrend.isPositive },
            trendCompareLabel: "vs prior week",
            iconColor: "bg-emerald-600",
          },
          {
            title: "Platform orders (WTD)",
            value: String(curOrders),
            icon: FileBarChart2,
            trend: { value: ordersTrend.value, isPositive: ordersTrend.isPositive },
            trendCompareLabel: "vs prior week",
            iconColor: "bg-purple-600",
          },
        ]);
      } catch {
        // ignore
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, fmtMoney]);

  const pieRows = useMemo(() => {
    if (!dashboardPayload?.business_performance?.length) return undefined;
    return dashboardPayload.business_performance.map((r) => ({
      business: r.business_name,
      sales: Number(r.total_revenue ?? 0),
    }));
  }, [dashboardPayload]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-foreground mb-1">Super Admin Dashboard</h1>
        <p className="text-muted-foreground">
          Manage business owners and monitor platform-wide performance.
        </p>
      </div>

      <DashboardCards
        cards={cards}
      />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <AdminSalesOverviewChart />
        <SalesByBusinessPieChart performanceRows={pieRows} />
      </div>

      <RecentActivity
        feed={mapRecentActivityToFeed(dashboardPayload?.recent_activity ?? []).slice(0, 5)}
        viewAllHref="/admin/recent-activity"
      />

      <AdminLatestTables
        dashboardRecentActivity={dashboardPayload?.recent_activity}
        businessRows={businessList}
      />
    </div>
  );
}
